'use client';

import { type ReactNode, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { cva } from 'class-variance-authority';

import { cn } from '../lib/cn';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';
type TooltipMode = 'default' | 'overflowSafe';

type TooltipProps = {
  children: ReactNode;
  className?: string;
  content: string;
  focusable?: boolean;
  mode?: TooltipMode;
  placement?: TooltipPlacement;
  showOnlyWhenTruncated?: boolean;
  wrap?: boolean;
};

type TooltipPosition = {
  transform: string;
  left?: number;
  top?: number;
};

const TOOLTIP_SURFACE_CLASS =
  'bg-[var(--color-tooltip-bg)] text-[var(--color-tooltip-fg)] shadow-[var(--shadow-tooltip)]';
const TOOLTIP_TEXT_CLASS = 'text-[11px] font-medium leading-4';
const TOOLTIP_BOX_CLASS = 'max-w-[350px] rounded-md px-2.5 py-1.5';
const TOOLTIP_NOWRAP_CLASS = 'overflow-hidden text-ellipsis whitespace-nowrap';
const TOOLTIP_WRAP_CLASS = 'whitespace-normal break-words';

const defaultTooltipVariants = cva(
  cn(
    'tooltip relative [--tt-bg:var(--color-tooltip-bg)] hover:z-[1040] focus-within:z-[1040]',
    'before:!z-[1040] before:!max-w-[350px] before:!overflow-hidden before:!text-ellipsis before:!rounded-md before:!px-2.5 before:!py-1.5 before:!text-[11px] before:!font-medium before:!leading-4 before:!text-[var(--color-tooltip-fg)] before:!shadow-[var(--shadow-tooltip)]',
  ),
  {
    variants: {
      placement: {
        top: 'tooltip-top',
        bottom: 'tooltip-bottom',
        left: 'tooltip-left',
        right: 'tooltip-right',
      },
      wrap: {
        false: 'before:!whitespace-nowrap',
        true: 'before:!whitespace-normal before:!break-words',
      },
    },
    defaultVariants: {
      placement: 'top',
      wrap: false,
    },
  },
);

const overflowSafeTooltipVariants = cva(
  cn(
    'pointer-events-none fixed z-[1040] overflow-visible',
    TOOLTIP_BOX_CLASS,
    TOOLTIP_SURFACE_CLASS,
    TOOLTIP_TEXT_CLASS,
    'after:absolute after:h-2 after:w-2 after:rotate-45 after:bg-[var(--color-tooltip-bg)]',
  ),
  {
    variants: {
      placement: {
        top: 'after:left-1/2 after:top-full after:-translate-x-1/2 after:-translate-y-1/2',
        bottom: 'after:bottom-full after:left-1/2 after:-translate-x-1/2 after:translate-y-1/2',
        left: 'after:left-full after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2',
        right: 'after:right-full after:top-1/2 after:translate-x-1/2 after:-translate-y-1/2',
      },
    },
    defaultVariants: {
      placement: 'top',
    },
  },
);

const overflowSafeContentVariants = cva('block', {
  variants: {
    wrap: {
      false: TOOLTIP_NOWRAP_CLASS,
      true: TOOLTIP_WRAP_CLASS,
    },
  },
  defaultVariants: {
    wrap: false,
  },
});

const getOverflowSafePosition = (rect: DOMRect, placement: TooltipPlacement): TooltipPosition => {
  const offset = 8;

  if (placement === 'left') {
    return {
      left: rect.left - offset,
      top: rect.top + rect.height / 2,
      transform: 'translate(-100%, -50%)',
    };
  }

  if (placement === 'top') {
    return {
      left: rect.left + rect.width / 2,
      top: rect.top - offset,
      transform: 'translate(-50%, -100%)',
    };
  }

  if (placement === 'bottom') {
    return {
      left: rect.left + rect.width / 2,
      top: rect.bottom + offset,
      transform: 'translate(-50%, 0)',
    };
  }

  return {
    left: rect.right + offset,
    top: rect.top + rect.height / 2,
    transform: 'translate(0, -50%)',
  };
};

const isTruncated = (element: HTMLElement): boolean => {
  const candidates = [element, ...Array.from(element.querySelectorAll<HTMLElement>('*'))];
  return candidates.some((candidate) => candidate.scrollWidth > candidate.clientWidth);
};

export const Tooltip = ({
  children,
  className,
  content,
  focusable = false,
  mode = 'default',
  placement = 'top',
  showOnlyWhenTruncated = false,
  wrap = false,
}: TooltipProps) => {
  const tooltipId = useId();
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const hasContent = content.trim().length > 0;

  if (!hasContent) {
    return <span className={className}>{children}</span>;
  }

  if (mode === 'overflowSafe') {
    const showTooltip = (target: HTMLElement) => {
      if (showOnlyWhenTruncated && !isTruncated(target)) {
        setPosition(null);
        return;
      }
      setPosition(getOverflowSafePosition(target.getBoundingClientRect(), placement));
    };

    return (
      <span
        className={cn('inline-flex', className)}
        onMouseEnter={(event) => showTooltip(event.currentTarget)}
        onMouseLeave={() => setPosition(null)}
        onFocus={(event) => showTooltip(event.currentTarget)}
        onBlur={() => setPosition(null)}
        aria-describedby={position ? tooltipId : undefined}
        tabIndex={focusable ? 0 : undefined}
      >
        {children}
        {position
          ? createPortal(
              <span
                id={tooltipId}
                role="tooltip"
                className={overflowSafeTooltipVariants({ placement })}
                style={{ left: position.left, top: position.top, transform: position.transform }}
              >
                <span className={overflowSafeContentVariants({ wrap })}>{content}</span>
              </span>,
              document.body,
            )
          : null}
      </span>
    );
  }

  return (
    <span
      className={cn(defaultTooltipVariants({ placement, wrap }), className)}
      data-tip={content}
      tabIndex={focusable ? 0 : undefined}
      aria-label={focusable ? content : undefined}
    >
      {children}
    </span>
  );
};
